/** @jsx jsx */
import { Box, Flex, jsx } from 'theme-ui'
import { FC } from 'react'
import { Form, Formik } from 'formik'
import ErrorFocus from '~/components/error-focus'
import useFormSubmission from '~/hooks/use-form-submission'
import { object, string } from 'yup'
import FormField from '~/components/form-field'
import { Button } from '~/components/button'
import ErrorMessage from '~/components/error-message'
import { navigate } from 'gatsby-link'
import urls from '~/common/urls'
import { useCustomerContext } from '~/context/customer-context'
import { useLocation } from '@reach/router'
import queryString from 'query-string'
import { trackSignIn } from '~/utils/marketing/pinterest'
import { graphql, useStaticQuery } from 'gatsby'
import { SiteMetaDataType } from '~/@types/models'

type FormValues = {
  email: string
  password: string
}

const initialValues: FormValues = {
  email: '',
  password: '',
}

const schema = object().shape({
  email: string().email().required(),
  password: string().required(),
})

type QueryResult = {
  site: {
    siteMetadata: SiteMetaDataType
  }
}

const SignIn = () => {
  const { signIn } = useCustomerContext()
  const location = useLocation()

  const query = useStaticQuery<QueryResult>(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)

  const siteUrl = query.site.siteMetadata.siteUrl

  const signInCallback = async (values: FormValues) => {
    await signIn(values, siteUrl)
    trackSignIn()

    const queriedTheme = queryString.parse(location.search)
    const { redirect } = queriedTheme

    // Ensure a valid expected value is passed
    if (['checkout'].includes(redirect as string)) {
      navigate(urls.purchase.checkout)
    } else {
      navigate(urls.account.accountPersonalData)
    }
  }

  const { onSubmit, isLoading, serverError } = useFormSubmission<FormValues, any>(signInCallback)

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={schema}>
      <Form data-cy="sign-in-form">
        <ErrorFocus />
        <FormField name="email" label="EMAIL ADDRESS" data-cy="email" />
        <FormField name="password" type="password" label="PASSWORD" data-cy="password" />
        <Flex as={Box} sx={{ flex: '1', my: [27], flexDirection: 'column' }}>
          <Button
            label={isLoading ? '...' : 'Login'}
            disabled={isLoading}
            type="submit"
            hasArrow={false}
            mobileBlock
          />
          {serverError && <ErrorMessage error={serverError} data-cy="server-error" />}
        </Flex>
      </Form>
    </Formik>
  )
}

export default SignIn
